/**
 * Auftragssuche: freie Begriffe wie bei einer Suchmaschine (alle Begriffe müssen passen, jeder in
 * irgendeinem Feld) plus gezielte Präfixe für Profis, z. B. `sku:NL-SER tracking:0034 land:AT`.
 */
export type OrderSearchField = "sku" | "tracking" | "email" | "plz" | "land" | "tag";

export interface ParsedOrderQuery {
  /** freie Begriffe, normalisiert (ohne führendes #) */
  terms: string[];
  /** gezielte Begriffe je Feld */
  fields: Partial<Record<OrderSearchField, string[]>>;
}

const ALIASES: Record<string, OrderSearchField> = {
  sku: "sku",
  artikel: "sku",
  tracking: "tracking",
  sendung: "tracking",
  email: "email",
  mail: "email",
  "e-mail": "email",
  plz: "plz",
  zip: "plz",
  land: "land",
  country: "land",
  tag: "tag",
};

/** Zerlegt die Eingabe; "in Anführungszeichen" bleibt ein Begriff (z. B. ganze Namen oder Straßen) */
export function parseOrderQuery(input: string): ParsedOrderQuery {
  const out: ParsedOrderQuery = { terms: [], fields: {} };
  const re = /(?:([\p{L}-]+):)?(?:"([^"]*)"|(\S+))/gu;
  for (const m of input.slice(0, 200).matchAll(re)) {
    const key = m[1]?.toLowerCase();
    const raw = (m[2] ?? m[3] ?? "").trim();
    // Präfix ohne Wert, z. B. während des Tippens "sku:" → noch nichts filtern
    if (!key && /^[\p{L}-]+:$/u.test(raw) && ALIASES[raw.slice(0, -1).toLowerCase()]) continue;
    const field = key ? ALIASES[key] : undefined;
    if (key && !field) {
      // unbekanntes Präfix (z. B. "Uhrzeit 10:30"): als normaler Begriff behandeln
      const whole = `${m[1]}:${raw}`.trim();
      if (whole.length) out.terms.push(whole);
      continue;
    }
    const value = raw.replace(/^#/, "");
    if (!value) continue;
    if (field) (out.fields[field] ??= []).push(field === "land" ? value.toUpperCase() : value);
    else out.terms.push(value);
  }
  out.terms = out.terms.slice(0, 8);
  return out;
}

export const isEmptyOrderQuery = (q: ParsedOrderQuery) => q.terms.length === 0 && Object.keys(q.fields).length === 0;

/** Für LIKE-Muster: Platzhalter im Suchbegriff wörtlich nehmen */
export const escapeLike = (s: string) => s.replace(/[\\%_]/g, (c) => `\\${c}`);
